import { Helmet } from "react-helmet-async";

const ORIGIN = "https://seprotection.com";
const BRAND_IMAGE = `${ORIGIN}/icon-512.png`;
const PROMO_IMAGE = `${ORIGIN}/img/promo/thanksgiving-2026-share.jpg`;
const PROMO_ENDS = Date.parse("2026-11-15T05:00:00Z");
const DEFAULT_IMAGE = Date.now() < PROMO_ENDS ? PROMO_IMAGE : BRAND_IMAGE;

/**
 * Reusable SEO component. Drop into any page.
 *
 * Props:
 *   title              — page-specific title (will be suffixed with brand)
 *   description        — page meta description (≤160 chars)
 *   path               — URL path of this page (e.g. "/training/md-wear-carry"). Used for canonical/og:url
 *   image              — absolute or path-relative og:image url (defaults to brand icon)
 *   type               — "website" | "article" | "product" (default "website")
 *   noindex            — true to add robots noindex (use for /admin etc.)
 *   schema             — JSON-LD object or array of objects to embed
 */
export default function Seo({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  schema,
}) {
  const url = `${ORIGIN}${path}`;
  const fullTitle = title
    ? `${title} | Secrecy Executive Protection`
    : "Secrecy Executive Protection — Elite Security & Training Academy";
  const desc = description ||
    "Maryland-based elite security firm and state-certified firearms training academy. Executive protection, residential & commercial security, healthcare security, armed special police, and concealed-carry permit training.";
  const ogImage = image.startsWith("http") ? image : `${ORIGIN}${image}`;
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Secrecy Executive Protection" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

/* ---------- Schema helpers ---------- */

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${ORIGIN}/#org`,
  name: "Secrecy Executive Protection",
  alternateName: "SEP",
  url: ORIGIN,
  logo: `${ORIGIN}/icon-512.png`,
  sameAs: [],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-410-645-0722",
    email: "info@seprotection.com",
    contactType: "customer service",
    areaServed: ["US"],
    availableLanguage: "en",
  },
};

export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "EducationalOrganization"],
  "@id": `${ORIGIN}/#business`,
  name: "Secrecy Executive Training Academy",
  legalName: "Secrecy Executive Protection LLC",
  foundingDate: "2024",
  description: "State-certified firearms and security training academy — Wear & Carry permits (7 states), Armed Security Officer certification, D.C. Special Police, and First Aid / CPR / AED. Taught by active-duty law-enforcement instructors.",
  image: `${ORIGIN}/icon-512.png`,
  url: ORIGIN,
  telephone: "+1-410-645-0722",
  email: "info@seprotection.com",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "4061 Powder Mill Rd, Suite 500-8",
    addressLocality: "Calverton",
    addressRegion: "MD",
    postalCode: "20705",
    addressCountry: "US",
  },
  openingHoursSpecification: [{
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    description: "By appointment only",
  }],
  areaServed: [
    { "@type": "State", name: "Maryland" },
    { "@type": "State", name: "New Jersey" },
    { "@type": "State", name: "District of Columbia" },
    { "@type": "State", name: "Virginia" },
    { "@type": "State", name: "Arizona" },
    { "@type": "State", name: "Florida" },
    { "@type": "State", name: "Utah" },
  ],
  hasMap: "https://www.google.com/maps?q=4061+Powder+Mill+Rd+Suite+500-8+Calverton+MD+20705",
  sameAs: [
    "https://share.google/kJZoxTVnDC9cUG3Xu",
    "https://g.page/r/CYETdfKWlMqQEAE",
    "https://www.instagram.com/secrecyexecutive",
    "https://www.facebook.com/profile.php?id=61592837862489",
  ],
};

export const GBP_REVIEW_URL = "https://g.page/r/CYETdfKWlMqQEAE/review";
export const GBP_PROFILE_URL = "https://share.google/kJZoxTVnDC9cUG3Xu";

export function courseSchema({ title, description, url, image, instructor = "S. Williams" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: title,
    description,
    url,
    image,
    provider: {
      "@type": "Organization",
      name: "Secrecy Executive Protection",
      sameAs: ORIGIN,
    },
    instructor: instructor ? { "@type": "Person", name: instructor } : undefined,
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "Blended",
      courseWorkload: "PT8H",
    },
  };
}

export function elearningCourseSchema({ title, description, slug, image }) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: title,
    description,
    url: `${ORIGIN}/elearning/${slug}`,
    image: image && image.startsWith("http") ? image : `${ORIGIN}${image}`,
    provider: {
      "@type": "Organization",
      name: "U.S. LawShield",
      sameAs: "https://www.uslawshield.com",
    },
    offers: { "@type": "Offer", category: "Online Course" },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "Online",
    },
  };
}

export function breadcrumbSchema(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${ORIGIN}${c.path}`,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

export function serviceSchema({ name, description }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    description,
    provider: { "@id": `${ORIGIN}/#business` },
    areaServed: { "@type": "Country", name: "United States" },
  };
}

export { ORIGIN };
